import React, { useState, useEffect, useRef } from 'react';
import { Clock } from 'lucide-react';

const CustomTimePicker = ({ name, value, onChange, compact = false, error = false }) => {
    const [isOpen, setIsOpen] = useState(false);
    const popoverRef = useRef(null);

    // Parse value (HH:mm in 24hr) to 12hr format
    const parseTime = (timeStr) => {
        if (!timeStr) return { h: '12', m: '00', ampm: 'PM' };
        const [hourStr, minStr] = timeStr.split(':');
        let h24 = parseInt(hourStr, 10);
        const m = minStr || '00';
        let ampm = 'AM';
        let h12 = h24;

        if (h24 >= 12) {
            ampm = 'PM';
            if (h24 > 12) h12 = h24 - 12;
        } else if (h24 === 0) {
            h12 = 12;
        }
        
        return { 
            h: h12.toString().padStart(2, '0'), 
            m: m.padStart(2, '0'), 
            ampm 
        };
    };

    const { h, m, ampm } = parseTime(value);

    // Handle clicks outside to close
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (popoverRef.current && !popoverRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };
        if (isOpen) document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    const updateTime = (newH, newM, newAmpm) => {
        let h24 = parseInt(newH, 10);
        if (newAmpm === 'PM' && h24 !== 12) h24 += 12;
        if (newAmpm === 'AM' && h24 === 12) h24 = 0;
        
        const outVal = `${h24.toString().padStart(2, '0')}:${newM}`;
        onChange({ target: { name, value: outVal } });
    };

    const hours = ['12', '01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11'];
    const minutes = Array.from({ length: 60 / 5 }, (_, i) => (i * 5).toString().padStart(2, '0'));

    return (
        <div ref={popoverRef} style={{ position: 'relative', width: compact ? '72px' : '140px', zIndex: isOpen ? 9999 : 1 }}>
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                style={{ 
                    width: '100%', 
                    padding: compact ? '3px 4px' : '0.7rem', 
                    borderRadius: compact ? '4px' : '8px', 
                    border: isOpen ? '1px solid #3b82f6' : (error ? '1px solid #ef4444' : '1px solid #cbd5e1'), 
                    background: '#ffffff', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    gap: compact ? '2px' : '0.5rem',
                    cursor: 'pointer', 
                    transition: 'all 0.2s',
                    boxShadow: isOpen ? '0 0 0 2px rgba(59,130,246,0.1)' : 'none',
                    height: compact ? '24px' : 'auto'
                }}
            >
                {!compact && <Clock size={16} color={isOpen ? '#3b82f6' : '#64748b'} />}
                <span style={{ 
                    fontWeight: 600, 
                    color: '#1e293b', 
                    fontSize: compact ? '0.72rem' : '0.95rem' 
                }}>
                    {value ? `${h}:${m} ${ampm}` : (compact ? '12:00 PM' : 'Time')}
                </span>
            </button>

            {isOpen && (
                <div
                    style={{
                        position: 'absolute', 
                        bottom: compact ? 'auto' : '100%', 
                        top: compact ? '100%' : 'auto', 
                        left: compact ? '-35px' : '0', 
                        marginBottom: compact ? '0' : '0.5rem',
                        marginTop: compact ? '0.25rem' : '0',
                        background: 'white', borderRadius: '12px', padding: '1rem',
                        boxShadow: '0 10px 25px rgba(0,0,0,0.1), 0 4px 6px rgba(0,0,0,0.05)',
                        border: '1px solid rgba(0,0,0,0.05)', zIndex: 9999,
                        display: 'flex', gap: '0.5rem', height: '200px'
                    }}
                >
                        {/* Hours List */}
                        <div className="custom-scrollbar" style={{ overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '0.25rem', paddingRight: '0.25rem' }}>
                            {hours.map(hour => (
                                <button
                                    key={`h-${hour}`}
                                    type="button"
                                    onClick={() => updateTime(hour, m, ampm)}
                                    style={{
                                        padding: '0.4rem 0.8rem', borderRadius: '6px',
                                        background: h === hour ? '#eff6ff' : 'transparent',
                                        color: h === hour ? '#2563eb' : '#475569',
                                        fontWeight: h === hour ? 700 : 500,
                                        border: 'none', cursor: 'pointer',
                                        transition: 'background 0.1s'
                                    }}
                                    onMouseEnter={e => { if(h !== hour) e.target.style.background = '#f1f5f9'; }}
                                    onMouseLeave={e => { if(h !== hour) e.target.style.background = 'transparent'; }}
                                >
                                    {hour}
                                </button>
                            ))}
                        </div>

                        {/* Divider */}
                        <div style={{ width: '1px', background: '#e2e8f0' }} />

                        {/* Minutes List */}
                        <div className="custom-scrollbar" style={{ overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '0.25rem', paddingRight: '0.25rem' }}>
                            {minutes.map(minute => (
                                <button
                                    key={`m-${minute}`}
                                    type="button"
                                    onClick={() => updateTime(h, minute, ampm)}
                                    style={{
                                        padding: '0.4rem 0.8rem', borderRadius: '6px',
                                        background: m === minute ? '#eff6ff' : 'transparent',
                                        color: m === minute ? '#2563eb' : '#475569',
                                        fontWeight: m === minute ? 700 : 500,
                                        border: 'none', cursor: 'pointer',
                                        transition: 'background 0.1s'
                                    }}
                                    onMouseEnter={e => { if(m !== minute) e.target.style.background = '#f1f5f9'; }}
                                    onMouseLeave={e => { if(m !== minute) e.target.style.background = 'transparent'; }}
                                >
                                    {minute}
                                </button>
                            ))}
                        </div>

                        {/* Divider */}
                        <div style={{ width: '1px', background: '#e2e8f0' }} />

                        {/* AM/PM */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                            {['AM', 'PM'].map(meridian => (
                                <button
                                    key={meridian}
                                    type="button"
                                    onClick={() => updateTime(h, m, meridian)}
                                    style={{
                                        padding: '0.4rem 0.8rem', borderRadius: '6px',
                                        background: ampm === meridian ? '#eff6ff' : 'transparent',
                                        color: ampm === meridian ? '#2563eb' : '#475569',
                                        fontWeight: ampm === meridian ? 700 : 500,
                                        border: 'none', cursor: 'pointer',
                                        transition: 'background 0.1s'
                                    }}
                                    onMouseEnter={e => { if(ampm !== meridian) e.target.style.background = '#f1f5f9'; }}
                                    onMouseLeave={e => { if(ampm !== meridian) e.target.style.background = 'transparent'; }}
                                >
                                    {meridian}
                                </button>
                            ))}
                        </div>
                </div>
            )}

            <style>{`
                .custom-scrollbar::-webkit-scrollbar { width: 4px; }
                .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
                .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
            `}</style>
        </div>
    );
};

export default CustomTimePicker;
